import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * LiveActivityTicker — tiny social-proof strip on the landing page.
 *
 * Hits GET /api/public/live-activity every 25 s and shows an anonymized
 * roll-up (bookings in the last hour + cities with activity + online pros).
 * Hides entirely when there's zero activity so new sites don't look dead.
 */
export default function LiveActivityTicker() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let killed = false;
        const fetch = async () => {
            try {
                const r = await api.get("/public/live-activity");
                if (!killed) setData(r.data);
            } catch {
                // Silent — ticker just hides.
            }
        };
        fetch();
        const t = setInterval(fetch, 25000);
        return () => {
            killed = true;
            clearInterval(t);
        };
    }, []);

    if (!data) return null;
    const hourly = data.bookings_last_hour || 0;
    const online = data.pros_online || 0;
    const cities = data.active_cities || [];
    if (hourly === 0 && online === 0 && cities.length === 0) return null;

    return (
        <div
            className="w-full bg-emerald-500/10 border-y border-emerald-400/20 py-2 overflow-hidden"
            data-testid="live-activity-ticker"
        >
            <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-3 flex-wrap text-[11px] uppercase tracking-[0.2em]">
                <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                <span className="text-emerald-300 font-medium" data-testid="ticker-live-label">LIVE</span>
                {hourly > 0 && (
                    <span className="text-muted-foreground">
                        <span className="gold-text" data-testid="ticker-hourly-count">{hourly}</span>{" "}
                        booking{hourly === 1 ? "" : "s"} this hour
                    </span>
                )}
                {cities.length > 0 && (
                    <span className="text-muted-foreground">
                        · active in <span className="gold-text">{cities.slice(0, 3).join(" · ")}</span>
                    </span>
                )}
                {online > 0 && (
                    <span className="text-muted-foreground">
                        · <span className="gold-text" data-testid="ticker-online-count">{online}</span> pro{online === 1 ? "" : "s"} online
                    </span>
                )}
            </div>
        </div>
    );
}
